<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api } from '@/api/client'
import StatusBadge from '@/components/StatusBadge.vue'
import type { ResourceRef } from '@/lib/resource-links'
import { cachedPreviewFetch } from '@/lib/preview-cache'

const props = defineProps<{ resourceRef: ResourceRef }>()

type JobRunRow = {
  id: number
  job_id: number
  status: string
  duration_ms: number | null
  error_message: string | null
}

const loading = ref(true)
const jobRun = ref<JobRunRow | null>(null)
let cancelled = false

const cacheKey = computed(() => `job_run:${props.resourceRef.id}`)

const duration = computed(() => {
  const ms = jobRun.value?.duration_ms
  if (ms == null) return null
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
})

onMounted(async () => {
  try {
    jobRun.value = await cachedPreviewFetch(cacheKey.value, () =>
      api.get<JobRunRow>(`/job-runs/${props.resourceRef.id}`),
    )
  } catch {
    jobRun.value = null
  } finally {
    if (!cancelled) loading.value = false
  }
})

onUnmounted(() => { cancelled = true })
</script>

<template>
  <div class="p-2 text-xs space-y-1.5 max-w-[280px] min-w-0">
    <p class="font-medium">Job run {{ resourceRef.id }}</p>
    <p v-if="loading" class="text-muted-foreground">Loading…</p>
    <template v-else-if="jobRun">
      <div class="flex items-center gap-1.5">
        <StatusBadge :status="jobRun.status" />
        <span v-if="duration" class="text-[10px] text-muted-foreground">{{ duration }}</span>
      </div>
      <p
        v-if="jobRun.error_message"
        class="font-mono text-[10px] text-muted-foreground break-all max-h-16 overflow-hidden border-t border-border/50 pt-1.5 mt-1"
      >
        {{ jobRun.error_message }}
      </p>
      <p class="text-[10px] text-muted-foreground">Open job run for task details →</p>
    </template>
    <p v-else class="text-muted-foreground">Job run #{{ resourceRef.id }}</p>
  </div>
</template>
